import type { StateCreator } from "zustand";
import type { liveSessionSlice } from "../../interfaces/slicesInterfaces/liveSessionSlice";
 

// Helper function to get session duration from localStorage
const getSessionDurationFromStorage = (): number => {
  try {
    return parseFloat(localStorage.getItem('sessionDurationInMinutes') || '0.00');
  
  } catch (error) {
    console.error('Error reading session duration from localStorage:', error);
    return 0.00;
  }
};

// Helper function to set session duration in localStorage
const setSessionDurationInStorage = (duration: number): void => {
  try {
    localStorage.setItem('sessionDurationInMinutes', duration.toString());
  } catch (error) {
    console.error('Error saving session duration to localStorage:', error);
  }
};

export const createLiveSessionSlice: StateCreator<
  liveSessionSlice,
  [],
  [],
  liveSessionSlice
> = (set, get) => ({
  sessionData: null,
  sessionError: null,
  currentSessionId: null, 
activeSlideIndex: (() => {
    const saved = localStorage.getItem("activeSlideIndex");
    return saved !== null && !isNaN(Number(saved)) ? Number(saved) : 0;
  })(),
  sessionDurationInMinutes: getSessionDurationFromStorage(),
  setSessionDurationInMinutes: (duration: number) => {
    setSessionDurationInStorage(duration);
    set({ sessionDurationInMinutes: duration });
  },

  setSessionData: (data) => set({ sessionData: data }),
  setSessionError: (error) => set({ sessionError: error }),
  setActiveSlideIndex: (index) => set({ activeSlideIndex: index }),

  startSession: async ({
    language,
    sessionId,
    materialId,
    materialMd5,
    userId,
    apiPost,
  }) => {
    const { currentSessionId } = get();

    console.log(`[LiveSessionStore] Starting session with:`, {
      language,
      sessionId,
      materialId,
      materialMd5,
      userId,
    });

    // ✅ Prevent duplicate API calls for the same session
    if (currentSessionId === sessionId) {
      console.log(
        `Session ${sessionId} already started, skipping duplicate call`
      );
      return;
    }

    // ✅ Validate required parameters
    if (!language || !materialId) {
      console.error("Missing required parameters:", {
        language,
        sessionId,
        materialId,
      });
      set({ sessionError: "Missing required parameters" });
      return;
    }

    if (!userId) {
      console.error("No user logged in");
      set({ sessionError: "User not authenticated" });
      return;
    }

    try {
      const endpoint = import.meta.env.VITE_API_START_SESSION_ENDPOINT.replace(
        "{userId}",
        userId
      ).replace("{sessionId}", sessionId);

      const response = await apiPost(
        endpoint,
        {},
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.status === 200) {
        const data = response.data;

        if (data?.livekit_response?.token && data?.firebase_token) {
          console.log("Session started successfully!");

          set({
            currentSessionId: sessionId,
            sessionData: {
              sessionId,
              language: language || "en",
              firebaseToken: data.firebase_token,
              livekitToken: data.livekit_response.token,
              materialId: materialId || data.material_id,
              materialMd5: materialMd5,
              actualStartDate: data.actual_start_date, 
              currentServerDate: data.current_server_date, 
            },
            sessionError: null,
          });
        } else {
          throw new Error("Invalid response: Missing required tokens");
        }
      } else {
        throw new Error(`Unexpected status: ${response.status}`);
      }
    } catch (err: any) {
      console.error("Error starting session:", err);

      const errorMessage =
        err.response?.data?.message || err.message || "Failed to start session";

      set({ sessionError: errorMessage });
    }
  },
});
